import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import moment from "moment";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { yellow, green, grey, blue } from "@material-ui/core/colors";
import { Button } from "@material-ui/core";
import { parseOrdersListToCSV } from "utils/parsers";
import PeriodSelect from "containers/PeriodSelect";
import DCSelect from "containers/DCSelect";
import { getOrdersList } from "actions/OrdersList.js";
import { hideDrawer, showDrawer } from "actions/Setting";
import { sendOrdersToAbbiamo, resendOrdersToAbbiamo } from "actions/Abbiamo";
import { sendNotification } from "actions/Notifications";
import AlertDialog from "components/AlertDialog";
import OrdersListDateSelect from "../components/OrdersListDateSelect.js";
import { resendReasonLabel, resendStatusLabel } from "../constants/resend";
import styles from "../styles.css";

const COOLDOWN_MS = 30000;

const getUniqueOptions = (ordersData, column) =>
  [...new Set(ordersData.map((order) => order[column]))].reduce(
    (acc, cur, i) => {
      acc[`${i}`] = cur;
      return acc;
    },
    {}
  );

const enumFormatter = (cell, row, enumObject) => enumObject[cell] || cell;

// Tooltips were temporarily disabled because they were affecting badly the performance
const clientFormatter = (cell, row) => (
  // <Tooltip title={`Tel: ${row.clientPhone}`} placement="top">
  <div>{cell}</div>
  // </Tooltip>
);

const localFormatter = (cell, row) =>
  row.justPayment ? "Apenas cobrança" : cell;

// const statusFormater = (type, cell, row, enumObject) => {
// 	const { startDate, endDate, user, boxId } = row[`${type}Status`];
// 	const startDateString = startDate && `Começo: ${new Date(startDate).toLocaleString('pt-BR')}`;
// 	const endDateString = endDate && `Término: ${new Date(endDate).toLocaleString('pt-BR')}`;
// 	const userString = user && `Autor: ${user}`;
// 	const boxIdString = boxId && `Id da caixa: ${boxId}`;
// 	const tooltipTitle = [startDateString, endDateString, userString, boxIdString]
// 		.filter(value => value)
// 		.join('\n');
// 	return (
// 		<Tooltip title={tooltipTitle} placement="top" classes={{ popper: styles.tooltip }}>
// 			<div>{enumFormatter(cell, row, enumObject)}</div>
// 		</Tooltip>
// 	);
// };

// const frozenStatusFormatter = (cell, row, enumObject) =>
// 	statusFormater('frozen', cell, row, enumObject);
// const snacksStatusFormatter = (cell, row, enumObject) =>
// 	statusFormater('snacks', cell, row, enumObject);

const themeStyles = (theme) => ({
  rowYellow: {
    backgroundColor: `${yellow[200]}`,
  },
  rowGreen: {
    backgroundColor: `${green[200]}`,
  },
  rowGrey: {
    backgroundColor: `${grey[400]}`,
  },
});

const selectRowProp = {
  mode: "checkbox",
  bgColor: blue[200],
  clickToSelect: true,
};

class OrdersListContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      date: moment().format("YYYY-MM-DD"),
      isCooldownActive: false,
      isSending: false,
      isResending: false,
      confirmOpen: false,
      resendConfirmOpen: false,
      resendResult: null,
      sendResult: null,
      selectedOrderIds: [],
    };

    this.cooldownTimer = null;
  }

  componentWillMount() {
    this.props.hideDrawer();
  }

  componentDidMount() {
    this.getOrdersList();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.period !== prevProps.period ||
      this.props.distributionCenter !== prevProps.distributionCenter ||
      this.state.date !== prevState.date
    ) {
      this.getOrdersList();
    }
  }

  componentWillUnmount() {
    this.props.showDrawer();
    if (this.cooldownTimer !== null) {
      clearTimeout(this.cooldownTimer);
    }
  }

  makeColumns() {
    const deliveryKindOptions = getUniqueOptions(
      this.props.ordersData,
      "deliveryKind"
    );
    const paymentStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "paymentStatusText"
    );
    const frozenStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "frozenStatusText"
    );
    const snacksStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "snacksStatusText"
    );
    const greenGrocerStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "greenGrocerStatusText"
    );
    const basketStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "basketStatusText"
    );
    const brandOptions = getUniqueOptions(
      this.props.ordersData.filter((order) => order.brandText),
      "brandText"
    );
    const abbiamoStatusOptions = getUniqueOptions(
      this.props.ordersData,
      "abbiamoStatus"
    );
    return [
      {
        text: "",
        className: styles.cell,
        columnClassName: `${styles.cell} ${this.props.classes.rowGrey}`,
        width: "50px",
        dataSort: true,
        dataFormat: (cell, row, formatExtraData, rowIndex) => rowIndex + 1,
      },
      {
        dataField: "routeNumber",
        text: "Rota",
        dataAlign: "center",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        rowSpan: true,
        width: "75px",
      },
      {
        dataField: "routeTypeText",
        text: "Tipo",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        width: "100px",
      },
      {
        dataField: "order",
        text: "Pedido",
        isKey: true,
        className: styles.cell,
        columnClassName: styles.cell,
        width: "85px",
        dataSort: true,
      },
      {
        dataField: "frozenLocation",
        text: "Cong. local",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        dataFormat: localFormatter,
        width: "80px",
      },
      {
        dataField: "snacksLocation",
        text: "Snacks local",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        dataFormat: localFormatter,
        width: "80px",
      },
      {
        dataField: "deliveryKind",
        text: "Tipo",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: deliveryKindOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: deliveryKindOptions,
      },
      {
        dataField: "brandText",
        text: "Origem",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "120px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: brandOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: brandOptions,
      },
      {
        dataField: "clientName",
        text: "Cliente",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "150px",
        dataFormat: clientFormatter,
      },
      {
        dataField: "clientPhone",
        text: "Telefone",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "150px",
      },
      {
        dataField: "observations",
        text: "Observações",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        width: "150px",
      },
      {
        dataField: "paymentStatusText",
        text: "Status do pagamento",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "150px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: paymentStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: paymentStatusOptions,
      },
      {
        dataField: "abbiamoStatus",
        text: "Status Abbiamo",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "150px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: abbiamoStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: abbiamoStatusOptions,
      },
      {
        dataField: "abbiamoResendStatus",
        text: "Reenvios",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "180px",
        dataSort: true,
        dataFormat: (cell, row) => resendStatusLabel(row),
      },
      {
        dataField: "productTypes",
        text: "Tipos de produtos",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
        width: "100px",
      },
      {
        dataField: "routeStatusText",
        text: "Status da rota",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "150px",
      },
      {
        dataField: "frozenStatusText",
        text: "Congelados status",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "160px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: frozenStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: frozenStatusOptions,
      },

      {
        dataField: "amountOfFrozenPackages",
        text: "Congelados Pacotes",
        className: styles.cell,
        columnClassName: styles.cell,
        dataSort: true,
      },
      {
        dataField: "snacksStatusText",
        text: "Snacks Status",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "160px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: snacksStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: snacksStatusOptions,
      },
      {
        dataField: "amountOfSnacksPackages",
        text: "Snacks Pacotes",
        className: styles.cell,
        columnClassName: styles.cell,
      },
      {
        dataField: "greenGrocerStatusText",
        text: "Quitanda Status",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "160px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: greenGrocerStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: greenGrocerStatusOptions,
      },
      {
        dataField: "amountOfGreenGrocerPackages",
        text: "Quitanda Pacotes",
        className: styles.cell,
        columnClassName: styles.cell,
      },
      {
        dataField: "basketStatusText",
        text: "Cestas Status",
        className: styles.cell,
        columnClassName: styles.cell,
        width: "160px",
        dataSort: true,
        filter: {
          type: "SelectFilter",
          options: basketStatusOptions,
        },
        filterFormatted: true,
        dataFormat: enumFormatter,
        formatExtraData: basketStatusOptions,
      },
      {
        dataField: "amountOfBasketPackages",
        text: "Cestas Pacotes",
        className: styles.cell,
        columnClassName: styles.cell,
      },
    ];
  }

  isRowUnselectable = (order) =>
    order.deliveryKind === "Retirar na Liv Up" ||
    (order.abbiamoStatus === "Integrado" && !order.resendEligible);

  getSelectedOrders = (predicate) =>
    this.props.ordersData.filter(
      (order) =>
        this.state.selectedOrderIds.includes(order.order) && predicate(order)
    );

  getSelectRowProp() {
    return {
      ...selectRowProp,
      selected: this.state.selectedOrderIds,
      unselectable: this.props.ordersData
        .filter(this.isRowUnselectable)
        .map((order) => order.order),
      onSelect: (row, isSelected) => {
        this.setState((prevState) => ({
          selectedOrderIds: isSelected
            ? [...prevState.selectedOrderIds, row.order]
            : prevState.selectedOrderIds.filter((id) => id !== row.order),
        }));
      },
      onSelectAll: (isSelected, rows) => {
        const ids = rows.map((r) => r.order);
        this.setState((prevState) => ({
          selectedOrderIds: isSelected
            ? [...new Set([...prevState.selectedOrderIds, ...ids])]
            : prevState.selectedOrderIds.filter((id) => !ids.includes(id)),
        }));
      },
    };
  }

  handleVisibleRowsChange = (_, visibleRows) => {
    const visibleIds = new Set(visibleRows.map((row) => row.order));
    this.setState((prevState) => {
      const filteredIds = prevState.selectedOrderIds.filter((id) =>
        visibleIds.has(id)
      );
      if (filteredIds.length === prevState.selectedOrderIds.length) {
        return null;
      }
      return { selectedOrderIds: filteredIds };
    });
  };

  startCooldown = () => {
    this.setState({ isCooldownActive: true });

    if (this.cooldownTimer !== null) {
      clearTimeout(this.cooldownTimer);
    }

    this.cooldownTimer = setTimeout(() => {
      this.setState({ isCooldownActive: false });
    }, COOLDOWN_MS);
  };

  getOrdersList = () => {
    this.props.getOrdersList(this.state.date);
    this.setState({ selectedOrderIds: [] });
    this.startCooldown();
  };

  // eslint-disable-next-line
  makeFooter() {
    return [
      [
        {
          label: "Total",
          columnIndex: 1,
        },
        {
          label: "Total value",
          columnIndex: 2,
          align: "right",
          formatter: (tableData) => <strong>{tableData.length}</strong>,
        },
      ],
    ];
  }

  rowClassNameFormat = row => {
    if (!['paid', 'on_delivery'].includes(row.paymentStatus)) {
      return this.props.classes.rowGrey;
    }
    if (
      (row.routeStatus === "allocating" && row.routeDispatched) ||
      (row.routeType !== "loggi" && row.routeDispatched) ||
      (!row.routeType && row.saleDispatched) ||
      (row.deliveryStatus && row.deliveryStatus === "in_transit")
    ) {
      return this.props.classes.rowGreen;
    }
    if (row.routeStatus === "allocating") {
      return this.props.classes.rowYellow;
    }

    return "";
  };

  handleDateSubmit = (date) => {
    this.setState({
      date,
    });
  };

  handleExportToCsv = () => {
    parseOrdersListToCSV(
      this.props.ordersData,
      `sales_${this.state.date}_${this.props.period}.csv`
    );
  };

  handleRefreshOrdersList = () => {
    this.getOrdersList();
  };

  handleOpenSendToAbbiamoDialog = () => {
    this.setState({ confirmOpen: true });
  };

  handleCloseSendToAbbiamoDialog = () => {
    this.setState({ confirmOpen: false });
  };

  handleSendToAbbiamo = async () => {
    if (this.state.isSending) {
      return;
    }

    const orders = this.getSelectedOrders(
      (order) =>
        order.deliveryKind !== "Retirar na Liv Up" &&
        order.abbiamoStatus !== "Integrado" &&
        !order.resendEligible
    ).map((order) => order.order);

    if (orders.length === 0) {
      this.setState({ confirmOpen: false });
      this.props.sendNotification({
        type: "info",
        message: "Nenhum pedido elegível para envio.",
      });
      return;
    }

    this.setState({ isSending: true, confirmOpen: false });
    try {
      const result = await this.props.sendOrdersToAbbiamo(orders);
      // Não atualizamos a lista automaticamente aqui para respeitar o cooldown
      // do botão "Atualizar lista" (AC-5): o usuário deve clicar nele para
      // ver o status refletido.
      this.setState({ selectedOrderIds: [], sendResult: result });
    } finally {
      this.setState({ isSending: false });
    }
  };

  handleCloseSendResult = () => {
    this.setState({ sendResult: null });
  };

  handleOpenResendToAbbiamoDialog = () => {
    this.setState({ resendConfirmOpen: true });
  };

  handleCloseResendToAbbiamoDialog = () => {
    this.setState({ resendConfirmOpen: false });
  };

  handleCloseResendResult = () => {
    this.setState({ resendResult: null });
  };

  handleResendToAbbiamo = async () => {
    if (this.state.isResending) {
      return;
    }

    const orders = this.getSelectedOrders(
      (order) => order.resendEligible === true
    ).map((order) => order.order);

    if (orders.length === 0) {
      this.setState({ resendConfirmOpen: false });
      this.props.sendNotification({
        type: "info",
        message: "Nenhum pedido elegível para reenvio.",
      });
      return;
    }

    this.setState({ isResending: true, resendConfirmOpen: false });
    try {
      const result = await this.props.resendOrdersToAbbiamo(orders);
      this.setState({ selectedOrderIds: [], resendResult: result });
      this.startCooldown();
    } finally {
      this.setState({ isResending: false });
    }
  };

  renderHeader() {
    const { date } = this.state;
    return (
      <Grid container direction="row" spacing={16}>
        <Grid item sm={4}>
          <DCSelect />
        </Grid>
        <Grid item sm={4}>
          <PeriodSelect />
        </Grid>
        <Grid item sm={4}>
          <OrdersListDateSelect
            selectedDate={date}
            onSelect={this.handleDateSubmit}
          />
        </Grid>
      </Grid>
    );
  }

  renderConfirmDialog() {
    return (
      <AlertDialog
        open={this.state.confirmOpen}
        title="Enviar pedidos para a Abbiamo"
        onClose={this.handleCloseSendToAbbiamoDialog}
        actions={[
          {
            onClick: this.handleCloseSendToAbbiamoDialog,
            title: "Cancelar",
            type: "default",
          },
          {
            onClick: this.handleSendToAbbiamo,
            title: "Enviar",
            variant: "contained",
            style: { backgroundColor: green[500], color: "#fff" },
            autoFocus: true,
            disabled: this.state.isSending,
          },
        ]}
      >
        Os pedidos selecionados serão enviados para a Abbiamo. Deseja
        continuar?
      </AlertDialog>
    );
  }

  renderResendConfirmDialog() {
    const selected = this.state.selectedOrderIds.length;
    const eligible = this.getSelectedOrders(
      (order) => order.resendEligible === true
    ).length;

    return (
      <AlertDialog
        open={this.state.resendConfirmOpen}
        title="Reenviar pedidos para a Abbiamo"
        onClose={this.handleCloseResendToAbbiamoDialog}
        actions={[
          {
            onClick: this.handleCloseResendToAbbiamoDialog,
            title: "Cancelar",
            type: "default",
          },
          {
            onClick: this.handleResendToAbbiamo,
            title: "Reenviar",
            variant: "contained",
            style: { backgroundColor: green[500], color: "#fff" },
            autoFocus: true,
            disabled: this.state.isResending,
          },
        ]}
      >
        {`Reenviar ${eligible} de ${selected} pedido(s) selecionado(s)? Será criado um novo pedido na Abbiamo, com a data de entrega atual, e o anterior receberá baixa.`}
      </AlertDialog>
    );
  }

  renderSendResultDialog() {
    const result = this.state.sendResult;
    if (!result) {
      return null;
    }

    const ignored = result.ignoredDetails || [];
    if (ignored.length === 0) {
      return null;
    }

    return (
      <AlertDialog
        open
        title="Pedidos não enviados"
        onClose={this.handleCloseSendResult}
        contentComponent="div"
        actions={[
          {
            onClick: this.handleCloseSendResult,
            title: "Fechar",
            type: "primary",
            autoFocus: true,
          },
        ]}
      >
        <div>{`${result.queued || 0} pedido(s) enfileirado(s) para envio.`}</div>
        <ul>
          {ignored.map(({ order, reason }) => (
            <li key={`${order}-${reason}`}>
              {`${order} — ${resendReasonLabel(reason)}`}
            </li>
          ))}
        </ul>
      </AlertDialog>
    );
  }

  renderResendResultDialog() {
    const result = this.state.resendResult;
    if (!result) {
      return null;
    }

    const notQueued = [
      ...(result.ignored || []),
      ...(result.conflicts || []),
      ...(result.failed || []),
    ];
    if (notQueued.length === 0) {
      return null;
    }

    return (
      <AlertDialog
        open
        title="Pedidos não enfileirados"
        onClose={this.handleCloseResendResult}
        contentComponent="div"
        actions={[
          {
            onClick: this.handleCloseResendResult,
            title: "Fechar",
            type: "primary",
            autoFocus: true,
          },
        ]}
      >
        <div>
          {`${result.queued || 0} pedido(s) enfileirado(s) para reenvio.`}
        </div>
        <ul>
          {notQueued.map(({ order, reason }) => (
            <li key={`${order}-${reason}`}>
              {`${order} — ${resendReasonLabel(reason)}`}
            </li>
          ))}
        </ul>
      </AlertDialog>
    );
  }

  renderTable() {
    const { ordersData, loading } = this.props;
    const { ordersPerRoute } = this.state;

    if (loading) {
      return (
        <Grid container spacing={16}>
          <Grid item sm={12}>
            <CircularProgress size={50} />
          </Grid>
        </Grid>
      );
    }

    const columns = this.makeColumns();
    return (
      <Grid container spacing={16}>
        <Grid item sm={12}>
          <Button
            variant="raised"
            component="span"
            color="primary"
            className="jr-btn text-blue-gray"
            onClick={this.handleExportToCsv}
          >
            <i className="zmdi zmdi-download" />
            &nbsp;&nbsp;Exportar
          </Button>
          <Button
            variant="raised"
            component="span"
            color="primary"
            className="jr-btn text-blue-gray"
            onClick={this.handleRefreshOrdersList}
            disabled={this.state.isCooldownActive}
          >
            <i className="zmdi zmdi-refresh" />
            &nbsp;&nbsp;Atualizar lista
          </Button>
          <Button
            variant="raised"
            component="span"
            color="primary"
            className="jr-btn text-blue-gray"
            onClick={this.handleOpenSendToAbbiamoDialog}
            disabled={this.state.isSending || this.state.selectedOrderIds.length === 0}
          >
            {this.state.isSending ? (
              <CircularProgress size={16} />
            ) : (
              <i className="zmdi zmdi-truck" />
            )}
            &nbsp;&nbsp;Enviar para Abbiamo
          </Button>
          <Button
            variant="raised"
            component="span"
            color="primary"
            className="jr-btn text-blue-gray"
            onClick={this.handleOpenResendToAbbiamoDialog}
            disabled={
              this.state.isResending ||
              this.getSelectedOrders((order) => order.resendEligible === true)
                .length === 0
            }
          >
            {this.state.isResending ? (
              <CircularProgress size={16} />
            ) : (
              <i className="zmdi zmdi-refresh-sync" />
            )}
            &nbsp;&nbsp;Reenviar para Abbiamo
          </Button>
          <BootstrapTable
            ref={(node) => {
              this.table = node;
            }}
            data={ordersData}
            search
            footerData={this.makeFooter()}
            footer
            version="4"
            trClassName={this.rowClassNameFormat}
            selectRow={this.getSelectRowProp()}
            options={{
              afterColumnFilter: this.handleVisibleRowsChange,
              afterSearch: this.handleVisibleRowsChange,
            }}
          >
            {columns.map((column) => (
              <TableHeaderColumn {...column} keyBoardNav>
                {column.text}
              </TableHeaderColumn>
            ))}
          </BootstrapTable>
        </Grid>
      </Grid>
    );
  }

  render() {
    return (
      <Grid container spacing={16}>
        <Grid item xs={12}>
          {this.renderHeader()}
          {this.renderTable()}
          {this.renderConfirmDialog()}
          {this.renderSendResultDialog()}
          {this.renderResendConfirmDialog()}
          {this.renderResendResultDialog()}
        </Grid>
      </Grid>
    );
  }
}

OrdersListContainer.propTypes = {
  getOrdersList: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.string,
  ordersData: PropTypes.array.isRequired,
  period: PropTypes.string,
  distributionCenter: PropTypes.string,
  hideDrawer: PropTypes.func.isRequired,
  showDrawer: PropTypes.func.isRequired,
  sendOrdersToAbbiamo: PropTypes.func.isRequired,
  resendOrdersToAbbiamo: PropTypes.func.isRequired,
  sendNotification: PropTypes.func.isRequired,
};

OrdersListContainer.defaultProps = {
  loading: false,
  success: false,
  error: null,
  period: null,
  distributionCenter: null,
};

const mapStateToProps = (state) => ({
  loading: state.ordersList.loading,
  success: state.ordersList.success,
  error: state.ordersList.error,
  ordersData: state.ordersList.data,
  period: state.periods.selected,
  distributionCenter: state.distributionCenters.selected,
});

export { OrdersListContainer };

export default compose(
  withStyles(themeStyles),
  connect(
    mapStateToProps,
    {
      getOrdersList,
      hideDrawer,
      showDrawer,
      sendOrdersToAbbiamo,
      resendOrdersToAbbiamo,
      sendNotification,
    }
  )
)(OrdersListContainer);
